import type { FilmProject, UploadedAssetManifest, UploadedAssetRecord } from "../types";

/**
 * The writer's/director's layer of a scene, read straight from the analysis the engine already
 * returns (apiPlan.scenes[] carries `dialogue` and an `ai` insight block; aiAnalysis carries the
 * logline). No new API contract — this only surfaces what the parse already knows.
 */

export interface SceneDialogueLine {
  speaker: string;
  text: string;
}

export interface SceneDirectorPage {
  emotionalBeat?: string;
  directorIntent?: string;
  visualLanguage?: string;
  productionDesign?: string;
  soundDesign?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeId(value: unknown): string {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function planScene(project: FilmProject, sceneId: string): Record<string, unknown> | null {
  const plan = isRecord(project.apiPlan) ? project.apiPlan : {};
  const scenes = Array.isArray(plan.scenes) ? plan.scenes : [];
  const wanted = normalizeId(sceneId);
  for (const scene of scenes) {
    if (!isRecord(scene)) continue;
    if (normalizeId(scene.sceneId) === wanted || normalizeId(scene.id) === wanted) return scene;
  }
  return null;
}

/** Actual screenplay lines for a scene: [{ speaker, text }]. Empty when the parse carried none. */
export function sceneDialogue(project: FilmProject, sceneId: string): SceneDialogueLine[] {
  const scene = planScene(project, sceneId);
  const lines = scene && Array.isArray(scene.dialogue) ? scene.dialogue : [];
  return lines
    .filter(isRecord)
    .map((line) => ({ speaker: String(line.speaker || "").trim(), text: String(line.text || "").trim() }))
    .filter((line) => line.text.length > 0);
}

/** The analysis's per-scene director notes (emotional beat, intent, visual language, sound). */
export function sceneDirectorPage(project: FilmProject, sceneId: string): SceneDirectorPage | null {
  const scene = planScene(project, sceneId);
  const ai = scene && isRecord(scene.ai) ? scene.ai : null;
  if (!ai) return null;
  const page: SceneDirectorPage = {
    emotionalBeat: String(ai.emotionalBeat || "").trim() || undefined,
    directorIntent: String(ai.directorIntent || "").trim() || undefined,
    visualLanguage: String(ai.visualLanguage || "").trim() || undefined,
    productionDesign: String(ai.productionDesign || "").trim() || undefined,
    soundDesign: String(ai.soundDesign || "").trim() || undefined
  };
  return Object.values(page).some(Boolean) ? page : null;
}

/** The film's logline from the analysis, with sensible fallbacks. */
export function projectLogline(project: FilmProject): string {
  const plan = isRecord(project.apiPlan) ? project.apiPlan : {};
  const ai = isRecord(plan.aiAnalysis) ? plan.aiAnalysis : {};
  const logline = String(ai.logline || "").trim();
  if (logline) return logline;
  const summary = String(ai.summary || "").trim();
  if (summary) return summary;
  return project.scenes[0]?.synopsis || project.scriptExcerpt.slice(0, 180);
}

/** Tone line for poster/pitch flavor. */
export function projectTone(project: FilmProject): string {
  const plan = isRecord(project.apiPlan) ? project.apiPlan : {};
  const ai = isRecord(plan.aiAnalysis) ? plan.aiAnalysis : {};
  return String(ai.tone || "").trim();
}

/* ---------- Voice-over matching ----------
 * VO exports are usually named after the line they read ("If_youre_one_of_the_millions_i_<voice>_<id>.mp3").
 * We match each voiceAudio upload to the dialogue line whose opening words it shares, which tells us
 * the scene it belongs to — so Watch mode can play the right read under the right boards. */

function words(value: string): string[] {
  return value
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
}

/** Score how well a VO filename matches a dialogue line: count of the line's opening words present, in order. */
function lineMatchScore(fileWords: string[], lineText: string): number {
  const lineWords = words(lineText).slice(0, 7);
  if (lineWords.length < 3) return 0;
  let index = 0;
  let hits = 0;
  for (const word of lineWords) {
    const found = fileWords.indexOf(word, index);
    if (found === -1) continue;
    hits += 1;
    index = found + 1;
  }
  return hits / lineWords.length;
}

export interface SceneVoiceTrack {
  asset: UploadedAssetRecord;
  /** Index of the matched dialogue line within the scene, for playback ordering. */
  lineIndex: number;
}

/**
 * Voice tracks for a scene. Two passes:
 * 1. Line match — the VO filename shares the opening words of one of this scene's dialogue lines.
 * 2. Sound-design fallback — VO that matches no line anywhere (e.g. commercial/narration reads)
 *    attaches to the scene whose director's-page sound design calls for VO/narration, else scene 1.
 */
export function voiceTracksForScene(
  project: FilmProject,
  sceneId: string,
  manifest?: UploadedAssetManifest
): SceneVoiceTrack[] {
  const assets = (manifest?.voiceAudio || []).filter((asset) => !asset.disabled && (asset.storageKey || asset.sourceUrl));
  if (!assets.length) return [];

  const bestPerAsset = assets.map((asset) => {
    const fileWords = words(asset.label || asset.fileName || "");
    let best = { score: 0, sceneId: "", lineIndex: -1 };
    for (const scene of project.scenes) {
      sceneDialogue(project, scene.id).forEach((line, lineIndex) => {
        const score = lineMatchScore(fileWords, line.text);
        if (score > best.score) best = { score, sceneId: scene.id, lineIndex };
      });
    }
    return { asset, best };
  });

  // Where do orphan reads (commercial copy, narration) belong? The scene that asked for VO.
  const voScene = project.scenes.find((scene) => {
    const sound = sceneDirectorPage(project, scene.id)?.soundDesign || "";
    return /\b(vo|voice[- ]?over|narration|commercial)\b/i.test(sound);
  }) || project.scenes[0];

  const tracks: SceneVoiceTrack[] = [];
  bestPerAsset.forEach(({ asset, best }, order) => {
    if (best.score >= 0.6) {
      if (best.sceneId === sceneId) tracks.push({ asset, lineIndex: best.lineIndex });
    } else if (voScene && voScene.id === sceneId) {
      tracks.push({ asset, lineIndex: 1000 + order }); // after any line-matched reads, in upload order
    }
  });
  return tracks.sort((a, b) => a.lineIndex - b.lineIndex);
}
